import { memo } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import type { Toast as ToastType } from '../types';

interface ToastProps {
  toast: ToastType;
  onRemove: (id: string) => void;
}

const iconMap = {
  success: CheckCircle,
  error: AlertCircle,
  info: Info,
  warning: AlertTriangle,
};

const styleMap = {
  success: 'toast-success',
  error: 'toast-error',
  info: 'toast-info',
  warning: 'bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800',
};

export const Toast = memo(function Toast({ toast, onRemove }: ToastProps) {
  const Icon = iconMap[toast.type];
  const { t } = useTranslation();

  // Error toasts ALWAYS render via i18n — NEVER the raw message. Falls back
  // to the generic key when no SanitizedError was attached, so even legacy
  // callers (showToast('error', title, rawString)) cannot leak technical
  // strings to the user. See project-error-sanitization-contract.
  const displayMessage =
    toast.type === 'error'
      ? t(toast.sanitized?.i18nKey ?? 'error.generic.service_unavailable')
      : toast.message;

  return (
    <div className={`${styleMap[toast.type]} animate-slide-up`}>
      <div className="flex items-start">
        <Icon className="h-5 w-5 mt-0.5 mr-3 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">{toast.title}</p>
          <p className="text-sm opacity-90 mt-1">{displayMessage}</p>
          {toast.sanitized?.errorId && (
            <p className="text-xs opacity-60 mt-1">
              {t('error.generic.support_reference', { id: toast.sanitized.errorId })}
            </p>
          )}
        </div>
        <button
          onClick={() => onRemove(toast.id)}
          className="ml-3 flex-shrink-0 rounded-md p-1.5 hover:bg-black hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-current"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
});

interface ToastContainerProps {
  toasts: ToastType[];
  onRemove: (id: string) => void;
}

export const ToastContainer = memo(function ToastContainer({
  toasts,
  onRemove,
}: ToastContainerProps) {
  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed right-4 z-50 flex flex-col gap-2 w-[calc(100vw-2rem)] sm:w-auto sm:max-w-sm pointer-events-none"
      style={{ top: 'calc(var(--safe-area-top, 0px) + 5rem)' }}
    >
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <Toast toast={toast} onRemove={onRemove} />
        </div>
      ))}
    </div>
  );
});
